import { useTranslation } from 'react-i18next';

import { Button } from '@/components/common/Button';
import { PanelNotice, PanelWaiting } from '@/components/hub/PanelNotice';
import { useTorneiDelGioco } from '@/hooks/useTorneiDelGioco';
import { CHESS_GAME_ID } from '@/lib/gameFeatures';
import { limuloFailure } from '@/lib/limulo/errors';
import { LimuloDegraded } from '@/routes/limulo/LimuloDegraded';
import { TorneiQuattroTab } from '@/routes/limulo/TorneiQuattroTab';

/**
 * Scheda «Tornei» degli SCACCHI su Limulo (E6 di ADR-0193, P3) — la lista dei SOLI tornei di scacchi del catalogo Limulo
 * (`profileType: 'chess'`), con le stesse quattro tab e la stessa riga dei tornei del backgammon.
 *
 * E' il gemello di `QgammonTournamentsPanel`: il filtro e i cinque vuoti stanno nel corpo comune `useTorneiDelGioco` (estratto
 * da P2 proprio per questo), qui si scrive solo cio' che ognuno dice. ⛔ «Nessun torneo di scacchi» e' il caso 5 e si dice SOLO
 * quando il server ha parlato per intero; senza catalogo, o con un catalogo senza scacchi (l'app non attivata
 * sull'organizzazione), e' il caso 4: «non disponibile», mai un vuoto.
 *
 * ⚠️ Il vuoto ha una chiave sua (`chess:tournaments.emptyPublished`): `chess:tournaments.empty` e' del vecchio `/chess` su
 * Lichess («Crea il primo»), e qui nessuno crea tornei.
 */
export function ChessTournamentsPanel() {
  const { t } = useTranslation('chess');
  const { t: tl } = useTranslation('limulo');
  const { state, tournaments, canLoadMore, loadingMore, loadMore, gameId } = useTorneiDelGioco({ gioco: CHESS_GAME_ID });

  if (state.kind === 'cannot-ask') return <PanelNotice tone="unavailable" />;

  const avviso =
    state.kind === 'waiting' ? (
      <PanelWaiting slow={state.slow} />
    ) : state.kind === 'no-answer' ? (
      <PanelNotice tone="error" />
    ) : state.kind === 'failed' ? (
      <LimuloDegraded failure={limuloFailure(state.error)} context="list" />
    ) : null;
  // `<Button>` comune (la primitiva: `tools/verify-bottoni.ts` non ammette `<button>` a mano nei file nuovi).
  const more = canLoadMore ? (
    <Button variant="secondary" fullWidth disabled={loadingMore} onClick={loadMore}>
      {loadingMore ? tl('list.loadingMore') : tl('list.loadMore')}
    </Button>
  ) : null;

  return (
    <div
      data-testid="chess-tornei-panel"
      className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto overscroll-contain py-2"
    >
      <TorneiQuattroTab
        pubblicati={state.kind === 'ok' || state.kind === 'empty' ? tournaments : null}
        avviso={avviso}
        coda={more}
        vuotoDelGioco={t('tournaments.emptyPublished')}
        gameIdMiei={gameId}
      />
    </div>
  );
}
